// ExamInterface.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Clock, ChevronLeft, ChevronRight, Flag, FlagOff, AlertCircle, CheckCircle, Bold } from 'lucide-react';
import RightNotesPanel from './RightNotesPanel'; // expects the inline-style RightNotesPanel.jsx

// 💻 Responsive layout constants tuned for 13–15" screens
// Fits cleanly within 1366px–1920px displays without horizontal scroll.

const NAV_WIDTH = 260;     // slightly narrower sidebar
const NOTES_WIDTH = 260;   // same width for visual symmetry
const CENTER_MAX = 920;    // question + options area (ample for code blocks)

// Total max width (1440px) fits 1080p screens perfectly
const MAX_WIDTH = CENTER_MAX + NAV_WIDTH + NOTES_WIDTH; // ~1440px


const ExamInterface = ({ questions = [], onSubmitExam, timeRemaining, setTimeRemaining, user, examConfig = {}, onLogout }) => {
  // core state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  // keep a ref to latest answers so auto-submit uses freshest state
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  // 🔒 Hardened anti-copy, anti-select, anti-right-click protection (only exam area)
  useEffect(() => {
    const disable = (e) => e.preventDefault();

    // Disable common copy/select events
    document.addEventListener('copy', disable);
    document.addEventListener('cut', disable);
    document.addEventListener('contextmenu', disable);
    document.addEventListener('selectstart', disable);

    // Inject CSS to make text unselectable, but keep inputs & textarea functional
    const style = document.createElement('style');
    style.innerHTML = `
      /* Disable text selection globally except form controls */
      body:not(input):not(textarea), 
      .nocopy, 
      .nocopy *:not(input):not(textarea) {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }

      /* Prevent selection highlighting */
      *::selection {
        background: transparent !important;
        color: inherit !important;
      }

      /* Prevent copying from code/pre tags */
      pre, code {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        pointer-events: none; /* disables right-click inside code */
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('copy', disable);
      document.removeEventListener('cut', disable);
      document.removeEventListener('contextmenu', disable);
      document.removeEventListener('selectstart', disable);
      document.head.removeChild(style);
    };
  }, []);

// ✅ Chapter label (only for Focused exams)
// const chapterLabel = (() => {
//   if (examConfig?.type !== 'focused') return null;
//   if (!examConfig?.chapter) return null;

//   // Examples:
//   // "Set 5"    -> Chapter 5
//   // "Set 3_2"  -> Chapter 3
//   const match = examConfig.chapter.match(/\d+/);
//   return match ? `Chapter ${match[0]}` : null;
// })();

// ✅ Certification Test label (for Certification exams)
const certificationLabel =
  examConfig?.type === 'certification' && examConfig?.testNumber
    ? `Certification Test #${examConfig.testNumber}`
    : null;


  // Timer effect (auto submit when time runs out)
  useEffect(() => {
    if (!questions || questions.length === 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // use answersRef to ensure latest answers are submitted
          try {
            onSubmitExam(answersRef.current);
          } catch (e) {
            console.error('Auto-submit error:', e);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [questions.length, setTimeRemaining, onSubmitExam]);

  // formatting helper for timer
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // handlers
 const handleAnswerSelect = (answerIndex) => {
  setAnswers(prev => {
    const current = prev[currentQuestion];
    const q = questions[currentQuestion];

  const isMultiSelect =
  (q.questionType === 'multiple-choice') ||
  (Array.isArray(q.correctAnswers) && q.correctAnswers.length > 1) ||
  (typeof q.correctAnswer === 'string' && q.correctAnswer.includes(',')) ||
  (/choose\s+(two|three|all)|select\s+(two|three|all)/i.test(q.question));

console.log('Q:', q.questionId, 'multi?', isMultiSelect, 'type=', q.questionType, 'correct=', q.correctAnswer);

    if (isMultiSelect) {
      // Multiple selection (checkbox style)
      const selected = Array.isArray(current) ? [...current] : [];
      const i = selected.indexOf(answerIndex);
      if (i >= 0) selected.splice(i, 1);
      else selected.push(answerIndex);
      return { ...prev, [currentQuestion]: selected };
    } else {
      // Single selection (radio style)
      return { ...prev, [currentQuestion]: answerIndex };
    }
  });
};

  const toggleMarkForReview = () => {
    setMarkedForReview(prev => {
      const n = new Set(prev);
      if (n.has(currentQuestion)) n.delete(currentQuestion);
      else n.add(currentQuestion);
      return n;
    });
  };

  const handleSubmit = () => {
    const unansweredCount = questions.length - Object.keys(answers).length;
    const markedCount = markedForReview.size;
    if (unansweredCount > 0 || markedCount > 0) {
      setShowSubmitWarning(true);
    } else {
      onSubmitExam(answers);
    }
  };

  // helper: question status for navigator
  const getQuestionStatus = (questionIndex) => {
    const isAnswered = Object.prototype.hasOwnProperty.call(answers, questionIndex);
    const isMarked = markedForReview.has(questionIndex);
    if (isAnswered && !isMarked) return 'answered';
    if (isAnswered && isMarked) return 'answered-marked';
    if (!isAnswered && isMarked) return 'marked';
    return 'unanswered';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return '#10b981';
      case 'answered-marked': return '#f59e0b';
      case 'marked': return '#ef4444';
      case 'unanswered': return '#e5e7eb';
      default: return '#e5e7eb';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered': return <CheckCircle size={16} color="white" />;
      case 'answered-marked': return <Flag size={16} color="white" />;
      case 'marked': return <Flag size={16} color="white" />;
      default: return null;
    }
  };

  // safe current question guard
  const currentQ = questions[currentQuestion] || { question: '', options: [], explanation: '', detailedExplanation: '', correctAnswer: null };

  // is practice mode
  const isPracticeMode = examConfig?.isPracticeMode ?? true;

  // mobile detection (simple)
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 768;

  // MOBILE NOTES SYNC: compute the same storage key used by RightNotesPanel
  // so mobile inline textarea reads/writes same notes.
  const notesStorageKey = (() => {
    const id = examConfig?.testNumber ?? examConfig?.id ?? examConfig?.examId ?? 'global';
    return `exam_notes_test_${id}`;
  })();

  // mobile notes state (keeps in sync with localStorage)
  const [mobileNotes, setMobileNotes] = useState(() => {
    try { return localStorage.getItem(notesStorageKey) || ''; } catch (e) { return ''; }
  });

  useEffect(() => {
    try { localStorage.setItem(notesStorageKey, mobileNotes); } catch (e) {}
  }, [mobileNotes, notesStorageKey]);

  // When examConfig changes (e.g., new test), reload mobile notes from localStorage
  useEffect(() => {
    try { setMobileNotes(localStorage.getItem(notesStorageKey) || ''); } catch (e) { setMobileNotes(''); }
  }, [notesStorageKey]);

  return (
    <div style={{ maxWidth: MAX_WIDTH, margin: '0 auto', padding: 20 }}>
      {/* ✅ MOBILE RESPONSIVE CSS - Only applies on small screens */}
      <style>{`
        @media (max-width: 768px) {
          .exam-main-grid {
            grid-template-columns: 1fr !important;
          }
          .exam-header {
            flex-direction: column !important;
            gap: 12px !important;
            align-items: stretch !important;
          }
          .exam-nav-buttons {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .exam-nav-buttons button {
            width: 100% !important;
            justify-content: center !important;
          }
          .navigator-panel {
            order: -1 !important;
          }
          .question-card {
            padding: 16px !important;
          }
          .notes-panel-desktop {
            display: none !important;
          }
        }
        
        @media (max-width: 480px) {
          .question-text-box {
            padding: 12px !important;
            font-size: 13px !important;
          }
          .option-button {
            padding: 12px !important;
          }
        }
      `}</style>

      {/* Header */}
      <div className="exam-header" style={{
        backgroundColor: 'white',
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 20 }}>
            Java Certification {isPracticeMode ? 'Practice' : 'Exam'}
          </h2>
          {/* <div style={{ fontSize: 14, color: '#64748b' }}>
            Question {currentQuestion + 1} of {questions.length}
            {isPracticeMode && <span style={{ color: '#10b981', fontWeight: 600, marginLeft: 8 }}>• Practice Mode</span>}
            {!isPracticeMode && <span style={{ color: '#667eea', fontWeight: 600, marginLeft: 8 }}>• Exam Mode</span>}
          </div> */}
          {/* <div style={{ fontSize: 14, color: '#64748b' }}>
  {chapterLabel && (
    <span style={{ fontWeight: 600, marginRight: 6 }}>
      {chapterLabel} •
    </span>
  )}
  Question {currentQuestion + 1} of {questions.length}
  {isPracticeMode && (
    <span style={{ color: '#10b981', fontWeight: 600, marginLeft: 8 }}>
      • Practice Mode
    </span>
  )}
  {!isPracticeMode && (
    <span style={{ color: '#667eea', fontWeight: 600, marginLeft: 8 }}>
      • Exam Mode
    </span>
  )}
</div> */}
<div style={{ fontSize: 14, color: '#64748b' }}>
  {certificationLabel && (
    <span style={{ fontWeight: 600, marginRight: 6 }}>
      {certificationLabel} •
    </span>
  )}
  Question {currentQuestion + 1} of {questions.length}

  {isPracticeMode ? (
    <span style={{ color: '#10b981', fontWeight: 600, marginLeft: 8 }}>
      • Practice Mode
    </span>
  ) : (
    <span style={{ color: '#667eea', fontWeight: 600, marginLeft: 8 }}>
      • Exam Mode
    </span>
  )}
</div>


        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          backgroundColor: timeRemaining < 300 ? '#fef2f2' : '#f8fafc',
          borderRadius: 12,
          border: `2px solid ${timeRemaining < 300 ? '#ef4444' : '#e2e8f0'}`
        }}>
          <Clock style={{ marginRight: 8, color: timeRemaining < 300 ? '#ef4444' : '#667eea' }} size={20} />
          <span style={{ fontSize: 18, fontWeight: 'bold', color: timeRemaining < 300 ? '#ef4444' : '#667eea' }}>
            {formatTime(timeRemaining)}
          </span>
        </div>
      </div>


  {isPracticeMode && !showExplanation && !answers.hasOwnProperty(currentQuestion) && (
  <div
    style={{
      marginTop: 8,
      padding: '10px 14px',
      backgroundColor: '#f8fafc',
      borderLeft: '4px solid #10b981',
      borderRadius: 6,
      fontSize: 18,
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      fontStyle: Bold
    }}
  >
    <span style={{ fontSize: 16 }}>💡</span>
    <span>
      Select an answer option to enable the{' '}
      <strong style={{ color: '#065f46' }}>Show Explanation</strong> button
    </span>
  </div>
)}


      {/* Grid: Question | Navigator | Notes */}
      <div className="exam-main-grid" style={{ display: 'grid', gridTemplateColumns: `1fr ${NAV_WIDTH}px ${NOTES_WIDTH}px`, gap: 20, alignItems: 'start' }}>
        {/* Question Column */}
        <div>
          {/* Question Card */}
          <div className="question-card" style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 32,
            marginBottom: 20,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
     {/* Question Header Row */}
{/* Question Header Block */}
<div
  style={{
    position: 'relative',
    marginBottom: 24,
  }}
>
  {/* Question Text (Full width) */}
  <div
    className="question-text-box"
    style={{
      fontSize: 15,
      lineHeight: '1.6',
      whiteSpace: 'pre-wrap',
      //fontFamily: `'Courier New', Consolas, 'JetBrains Mono', monospace`,
      fontFamily: `'JetBrains Mono', Consolas, 'Courier New', monospace`,
      fontWeight: 'normal',
      backgroundColor: '#f8fafc',
      padding: '16px 20px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      color: '#1e293b',
    }}
  >
    {currentQ.question}
  </div>

  {/* Mark for Review Button — floats top-right above the box */}
  <button
    onClick={toggleMarkForReview}
    style={{
      position: 'absolute',
      top: '-10px',
      right: '-8px',
      padding: '6px 12px',
      border: `2px solid ${markedForReview.has(currentQuestion) ? '#ef4444' : '#cbd5e1'}`,
      backgroundColor: markedForReview.has(currentQuestion) ? '#fef2f2' : 'white',
      color: markedForReview.has(currentQuestion) ? '#ef4444' : '#475569',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 500,
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'all 0.2s ease',
    }}
  >
    {markedForReview.has(currentQuestion) ? 'Unmark' : 'Mark for Review'}
  </button>
</div>


            {/* Options */}
            <div>
              {currentQ.options.map((option, idx) => {
                const sel = answers[currentQuestion];
                const isSelected = Array.isArray(sel) ? sel.includes(idx) : sel === idx;
                return (
                  <button key={idx} onClick={() => handleAnswerSelect(idx)} className="option-button" style={{
                    backgroundColor: isSelected ? '#667eea' : 'white',
                    border: `2px solid ${isSelected ? '#667eea' : '#e2e8f0'}`,
                    borderRadius: 12,
                    padding: 16,
                    margin: '8px 0',
                    cursor: 'pointer',
                    fontSize: 16,
                    fontFamily: `'JetBrains Mono', Consolas, 'Courier New', monospace`,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    textAlign: 'left',
                    color: isSelected ? 'white' : 'inherit',
                    boxShadow: isSelected ? '0 4px 15px rgba(102,126,234,0.3)' : 'none'
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#f1f5f9', color: isSelected ? 'white' : '#64748b', fontWeight: 'bold', marginRight: 16
                    }}>
                      {String.fromCharCode(65 + idx)}
                    </div>
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="exam-nav-buttons" style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 20,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))} disabled={currentQuestion === 0} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px',
              border: currentQuestion === 0 ? '2px solid #e2e8f0' : '2px solid #667eea',
              backgroundColor: currentQuestion === 0 ? '#f8fafc' : 'white',
              color: currentQuestion === 0 ? '#94a3b8' : '#667eea',
              borderRadius: 8, cursor: currentQuestion === 0 ? 'not-allowed' : 'pointer', fontSize: 16, fontWeight: 600
            }}>
              <ChevronLeft size={20} />
              Previous
            </button>

            {answers.hasOwnProperty(currentQuestion) && (
              <button onClick={() => isPracticeMode ? setShowExplanation(true) : null} disabled={!isPracticeMode} style={{
                padding: '12px 20px',
                border: `2px solid ${isPracticeMode ? '#10b981' : '#e2e8f0'}`,
                backgroundColor: isPracticeMode ? 'white' : '#f8fafc',
                color: isPracticeMode ? '#10b981' : '#94a3b8',
                borderRadius: 8,
                cursor: isPracticeMode ? 'pointer' : 'not-allowed',
                fontSize: 16,
                fontWeight: 600
              }}>
                {isPracticeMode ? 'Show Explanation' : 'Show Explanation (Exam Mode)'}
              </button>
            )}

            {currentQuestion === questions.length - 1 ? (
              <button onClick={handleSubmit} style={{ padding: '12px 20px', border: 'none', backgroundColor: '#ef4444', color: 'white', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>
                Submit Exam
              </button>
            ) : (
              <button onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', border: '2px solid #667eea', backgroundColor: 'white', color: '#667eea', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>
                Next
                <ChevronRight size={20} />
              </button>
            )}
          </div>

          {/* Explanation Section */}
          {/* {showExplanation && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: 16,
              padding: 24,
              marginTop: 20,
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, color: '#10b981', fontSize: 14 }}>Explanation</h3>
                <button onClick={() => setShowExplanation(false)} style={{ background: 'none', border: '2px solid #10b981', color: '#10b981', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Close</button>
              </div>

              <div style={{ backgroundColor: '#eaedeeff', border: '1px solid #0369a1', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 14, fontWeight: 600, color: '#0369a1' }}>
                Correct Answer: {(() => {
                  const ca = currentQ.correctAnswer;
                  if (ca === undefined || ca === null || ca === -1 || ca === "-1") return 'Not available (check DynamoDB data)';
                  if (typeof ca === 'string') return ca.split(',').map(a => a.trim()).join(', ');
                  if (Array.isArray(ca)) return ca.join(', ');
                  return String(ca);
                })()}
              </div>

              <div style={{ backgroundColor: '#f0fdf4', padding: 16, borderRadius: 8, borderLeft: '4px solid #10b981' }}>
                <p style={{ margin: 0, lineHeight: '1.6', color: '#166534' }}>{currentQ.explanation}</p>
                {currentQ?.detailedExplanation && <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #dcfce7' }}><p style={{ margin: 0, lineHeight: '1.6', color: '#166534', fontSize: 14 }}>{currentQ.detailedExplanation}</p></div>}
              </div>
            </div>
          )} */}

          {/* Explanation Section */}
{showExplanation && (
  <div style={{
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    marginTop: 20,
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
      <h3 style={{ margin: 0, color: '#10b981', fontSize: 18, fontWeight: '600' }}>Explanation</h3>
      <button onClick={() => setShowExplanation(false)} style={{ background: 'none', border: '2px solid #10b981', color: '#10b981', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Close</button>
    </div>

    <div style={{ backgroundColor: '#eff3f4ff', border: '1px solid #0369a1', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 15, fontWeight: 600, color: '#0a0b0bff' }}>
      Correct Answer: {(() => {
        const ca = currentQ.correctAnswer;
        if (ca === undefined || ca === null || ca === -1 || ca === "-1") return 'Not available (check DynamoDB data)';
        if (typeof ca === 'string') return ca.split(',').map(a => a.trim()).join(', ');
        if (Array.isArray(ca)) return ca.join(', ');
        return String(ca);
      })()}
    </div>

    {/* Simple Explanation */}
    <div style={{ backgroundColor: '#ffffffff', padding: 16, borderRadius: 8, borderLeft: '4px solid #10b981', marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 12px 0', fontSize: 15, fontWeight: '600', color: '#2dc40bff' }}>
         Simple Explanation
      </h4>
      <p
  style={{
    margin: 0,
    lineHeight: '1.8',
    fontSize: 18,
    fontFamily: `'Courier New','JetBrains Mono', 'Consolas',  monospace`,
    fontWeight: 500,
    color: '#0c0c0cff',
    whiteSpace: 'pre-wrap'
  }}
>
  {currentQ.explanation}
</p>
    </div>

    {/* Detailed Explanation - Only show if available */}
    {currentQ?.detailedExplanation && currentQ.detailedExplanation !== currentQ.explanation && (
      <div style={{ backgroundColor: '#ffffffff', padding: 16, borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: 15, fontWeight: '600', color: '#1eca18ff' }}>
          Detailed Explanation
        </h4>
        {/* <p style={{ margin: 0, lineHeight: '1.7', color: '#92400e', fontSize: 15 }}>
          {currentQ.detailedExplanation}
        </p> */}
        <p
  style={{
    margin: 0,
    lineHeight: '1.8',
    fontSize: 18,
    fontFamily: `'Courier New','JetBrains Mono', 'Consolas', monospace`,
    fontWeight: 500,
    color: '#070707ff',
    whiteSpace: 'pre-wrap'
  }}
>
  {currentQ.detailedExplanation}
</p>

      </div>
    )}
  </div>
)}
        </div>

        {/* Navigator Column */}
        <div className="navigator-panel" style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.1)', height: 'fit-content' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 14 }}>Question Navigator</h3>

          {/* Legend */}
          <div style={{ marginBottom: 20, fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><div style={{ width: 16, height: 16, backgroundColor: '#10b981', borderRadius: 4 }}></div><span>Answered</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><div style={{ width: 16, height: 16, backgroundColor: '#f59e0b', borderRadius: 4 }}></div><span>Answered + Marked</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><div style={{ width: 16, height: 16, backgroundColor: '#ef4444', borderRadius: 4 }}></div><span>Marked for Review</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ width: 16, height: 16, backgroundColor: '#e5e7eb', borderRadius: 4 }}></div><span>Not Answered</span></div>
          </div>

          {/* Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8 }}>
            {questions.map((_, idx) => {
              const status = getQuestionStatus(idx);
              const isCurrent = idx === currentQuestion;
              return (
                <button key={idx} onClick={() => setCurrentQuestion(idx)} aria-current={isCurrent ? 'true' : undefined} style={{
                  width: 40, height: 40, borderRadius: 8, border: isCurrent ? '3px solid #1e293b' : '2px solid transparent',
                  backgroundColor: getStatusColor(status), color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {getStatusIcon(status) || (idx + 1)}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 20, padding: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}><strong>Progress Summary:</strong></div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Answered: {Object.keys(answers).length}/{questions.length}<br/>
              Marked for Review: {markedForReview.size}<br/>
              Remaining: {questions.length - Object.keys(answers).length}
            </div>
          </div>
        </div>

        {/* Notes column */}
        {/* Desktop: RightNotesPanel renders the nice panel; Mobile: we show inline textarea below */}
        {!isNarrow && <div className="notes-panel-desktop"><RightNotesPanel examConfig={examConfig} panelWidth={NOTES_WIDTH} /></div>}
      </div>

      {/* Mobile inline notes fallback (saves to same localStorage key used by RightNotesPanel) */}
      {isNarrow && (
        <div style={{ marginTop: 20 }}>
          <div style={{ background: 'rgba(255,255,255,0.98)', borderRadius: 10, padding: 8, border: '1px solid rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Notes</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>({examConfig?.testNumber ?? 'global'})</div>
            </div>
            <textarea
              aria-label={`Mobile notes ${notesStorageKey}`}
              value={mobileNotes}
              onChange={(e) => setMobileNotes(e.target.value)}
              placeholder="Notes (mobile) — saved locally"
              style={{ width: '100%', height: 120, padding: 8, borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <button onClick={() => { setMobileNotes(''); try { localStorage.removeItem(notesStorageKey); } catch (e) {} }} style={{ fontSize: 13, color: '#374151', border: 'none', background: 'transparent', cursor: 'pointer' }}>Clear</button>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Saved locally</div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Warning Modal */}
      {showSubmitWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 32, maxWidth: 500, margin: 20, textAlign: 'center' }}>
            <AlertCircle style={{ color: '#f59e0b', margin: '0 auto 16px auto' }} size={64} />
            <h3 style={{ fontSize: 16, fontWeight: 'bold', margin: '0 0 16px 0' }}>Review Your Answers</h3>
            <div style={{ color: '#64748b', marginBottom: 24, lineHeight: '1.6' }}>
              <p>You have:</p>
              <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                <li>{questions.length - Object.keys(answers).length} unanswered questions</li>
                <li>{markedForReview.size} questions marked for review</li>
              </ul>
              <p>Are you sure you want to submit?</p>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setShowSubmitWarning(false)} style={{ padding: '12px 24px', border: '2px solid #e2e8f0', backgroundColor: 'white', color: '#64748b', borderRadius: 8, cursor: 'pointer' }}>
                Continue Review
              </button>
              <button onClick={() => { setShowSubmitWarning(false); onSubmitExam(answersRef.current); }} style={{ padding: '12px 24px', border: 'none', backgroundColor: '#ef4444', color: 'white', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamInterface;